import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { EnvService } from '@lib/env';
import { UriBuilder } from 'uribuilder';
import { catchError, finalize, Observable, of, Subject, tap } from 'rxjs';
import { IDemand, IDemandErpTransmissionData } from './types/Demand';
import { DemandProduct, DemandProductDto } from './types/DemandProduct';
import { IDemandProductChangeResponse } from './types/IDemandProductChangeResponse';
import { IDemandId } from './types/IDemandId';
import { IDemandDraftFromPdfOutcome } from './types/IDemandDraftFromPdfOutcome';
import { DemandAddSupplierRequest } from './types/DemandAddSupplierRequest';
import { ShippingAddress } from '@lib/address';
import {
    DemandItemRemoved,
    DemandShippingAddressChange,
    IDemandItemRemoved,
    IDemandShippingAddressState,
} from './types/DemandShippingAddressChange';
import {
    AfterLogin,
    AfterLoginInit,
    afterLoginInit,
    EprocNavigationModule,
    HttpExtras,
    NavigationHint,
    NavigationHints,
    NavigationHintService,
    SseDataType,
    SseService,
    View,
    NotificationService,
} from '@lib/core';
import { DemandMetadataChangeRequest } from './types/DemandMetadataChangeRequest';
import { DemandSupplier, IDemandSupplier } from './types/IDemandSupplierContact';
import { map, switchMap, take } from 'rxjs/operators';
import { IUnannouncedDemandExists } from './types/IUnannouncedDemandExists';
import { Dayjs } from 'dayjs';
import { ITransmissionValue } from '@lib/client';
import { DemandSendToInternalErpStatus } from './display/content/types/DemandSendToInternalErpStatus';
import { DemandSendToSupplierStatus } from './display/content/types/DemandSendStatus';
import { RfqSettingsService } from '../rfq-settings.service';
import { IPurchaseRequisitionActive } from '../purchase-requisition/types/IPurchaseRequisitionActive';
import { I18nService } from '@lib/i18n';
import { DemandItemAdditionalCostInfo, IDemandAdditionalCostInfo } from './types/IDemandItemAdditionalCost';
import { DemandAnnounceResult, IDemandAnnounceResult } from './types/IDemandAnnounceResult';
import { ValidationSubject } from './validation/types/IValidationError';
import { ISupplierEmailValidationResponse } from './display/content/types/ISupplierEmailValidationResponse';
import { DemandSelectableCurrencies, IDemandSelectableCurrencies } from './types/IDemandSelectableCurrencies';

interface ICreateDemandFromRfqRequest {
    readonly rfqId: number;
    readonly fromRfqItems: boolean;
    /** Only for quotation items: restricts the new demand to the chosen ones. */
    readonly selectedQuotationItemIds?: number[];
}

@Injectable({
    providedIn: 'root',
})
@AfterLogin()
export class DemandDataService implements AfterLoginInit {
    private readonly http = inject(HttpClient);
    private readonly env = inject(EnvService);
    private readonly sseService = inject(SseService);
    private readonly navigationHintService = inject(NavigationHintService);
    private readonly rfqSettingsService = inject(RfqSettingsService);
    private readonly i18n = inject(I18nService);
    private readonly notificationService = inject(NotificationService);

    private readonly _pdfAnalysisRunning = signal(false);
    /** True while an asynchronous PDF analysis (`analyzePdfToDraft`) is in progress. */
    readonly pdfAnalysisRunning = this._pdfAnalysisRunning.asReadonly();

    private readonly _unannouncedDemandChanged$ = new Subject<void>();
    /** Emits when the existence of an unannounced demand draft may have changed (e.g. after a PDF analysis). */
    readonly unannouncedDemandChanged$ = this._unannouncedDemandChanged$.asObservable();

    constructor() {
        afterLoginInit(this, View.CLIENT);
    }

    /**
     * Complete a demand and announce its existence to everyone involved
     */
    public announce(id: string): Observable<DemandAnnounceResult> {
        return this.http.post<IDemandAnnounceResult>(this.getBase(id, 'announce').toString(), {}).pipe(
            map((dto) => new DemandAnnounceResult(dto, this.i18n)),
            catchError((errorResponse: HttpErrorResponse) => {
                if (errorResponse.error?.error?.subject === ValidationSubject.DEMAND) {
                    return of(new DemandAnnounceResult(errorResponse.error, this.i18n));
                }

                throw errorResponse;
            }),
        );
    }

    /**
     * Complete a demand that results directly in an rfq
     */
    public convertToRfq(id: string): Observable<void> {
        return this.http.post<void>(this.getBase(id, 'to-rfq').toString(), {});
    }

    public clearCurrentUnannouncedDemand(): Observable<void> {
        return this.http.delete<void>(this.getBase().toString());
    }

    /**
     * Retrieve a new demand. If there is a valid and unannounced demand for the user, this will be returned instead
     */
    public getNew(): Observable<IDemand> {
        return this.http.get<IDemand>(this.getBase().toString(), { params: HttpExtras.IGNORE_CACHE });
    }

    /**
     * Retrieve a demand by id.
     * @param id
     */
    public getById(id: string): Observable<IDemand> {
        return this.http.get<IDemand>(this.getBase(id).toString(), { params: HttpExtras.IGNORE_CACHE });
    }

    /**
     * Adds a product to the current demand. Will return an error response if no WIP demand exists for the current user
     * @param id
     * @param product
     */
    public addProduct(id: string, product: DemandProduct): Observable<IDemandProductChangeResponse> {
        return this.http.post<IDemandProductChangeResponse>(this.getBase(id, 'product').toString(), product.toDto());
    }

    /**
     * Save the metadata information
     * @param id
     * @param title
     * @param messageToSupplier
     * @param internalMessage
     * @param conditions
     * @param validUntil
     */
    public saveMetaData(
        id: string,
        title: string,
        messageToSupplier: string,
        internalMessage: string,
        conditions: string,
        validUntil?: Dayjs,
    ): Observable<void> {
        return this.http.post<void>(
            this.getBase(id, 'additional-information').toString(),
            new DemandMetadataChangeRequest(title, messageToSupplier, internalMessage, conditions, validUntil),
        );
    }

    public saveTransmissionIngo(demandId: string, transmissionInfo: ITransmissionValue): Observable<void> {
        const data: IDemandErpTransmissionData = {
            sendToInternalErpStatus: transmissionInfo.preventTransferToClientErp
                ? DemandSendToInternalErpStatus.UNWANTED
                : DemandSendToInternalErpStatus.POREQUEST,
            sendToSupplierStatus: transmissionInfo.preventTransferToSupplier
                ? DemandSendToSupplierStatus.UNWANTED
                : DemandSendToSupplierStatus.UNSENT,
        };
        const uri: UriBuilder = this.env.getBase('demand', demandId, 'transmission', 'erp');
        return this.http.put<void>(uri.toString(), data);
    }

    /**
     * Will change the product data of a given product for the current demand. If no WIP demand exists for the current user
     * it will return an error response.
     * @param id
     * @param product
     */
    public editProduct(id: string, product: DemandProduct): Observable<IDemandProductChangeResponse> {
        return this.http.put<IDemandProductChangeResponse>(this.getBase(id, 'product').toString(), product.toDto());
    }

    /**
     * Removes a product from the current demand
     * @param id
     * @param productId
     */
    public removeProduct(id: string, productId: string): Observable<DemandItemRemoved> {
        return this.http
            .delete<IDemandItemRemoved>(this.getBase(id, 'product', productId).toString())
            .pipe(map((dto) => new DemandItemRemoved(dto)));
    }

    /**
     * Currencies this demand may be written in (EPROC-11425): the currencies the customer shares
     * with every supplier already assigned to the demand. Feeds the currency selector of a position,
     * the supplier-currency state (EPROC-11425/11560 Konsolidierung: one endpoint serves banner,
     * supplier filter and order-release gate) and the release validation, so it must never come from
     * the cache — assigning or removing a supplier changes the result.
     * @param demandId - demand id
     */
    public getSelectableCurrencies(demandId: string): Observable<DemandSelectableCurrencies> {
        return this.http
            .get<IDemandSelectableCurrencies>(this.getBase(demandId, 'selectable-currencies').toString(), {
                params: HttpExtras.IGNORE_CACHE,
            })
            .pipe(map((dto) => new DemandSelectableCurrencies(dto)));
    }

    /**
     * Validates supplier email
     * @param supplierGroupId - The supplier company ID
     */
    public validateSupplierEmail(demandId: string): Observable<ISupplierEmailValidationResponse> {
        const uri: UriBuilder = this.env.getBase('demand', demandId, 'validate-email');
        return this.http.get<ISupplierEmailValidationResponse>(uri.toString(), { params: HttpExtras.IGNORE_CACHE });
    }

    public updateDeliveryDateForAllItems(id: string, desiredDeliveryDate: Dayjs): Observable<void> {
        const uri: UriBuilder = this.getBase(id, 'delivery-date');
        return this.http.post<void>(uri.toString(), {
            desiredDeliveryDate: desiredDeliveryDate.format('YYYY-MM-DD'),
        });
    }

    /**
     * Adds a supplier with contact to the current demand
     * @param id
     * @param supplierCompanyId
     * @param contactId
     */
    public addSupplier(id: string, supplierCompanyId: string, contactId: string): Observable<DemandSupplier[]> {
        return this.http
            .post<
                IDemandSupplier[]
            >(this.getBase(id, 'supplier').toString(), new DemandAddSupplierRequest(supplierCompanyId, contactId))
            .pipe(map((suppliers) => suppliers.map((s) => DemandSupplier.instance(s, this.i18n))));
    }

    /**
     * Removes a supplier from the demand
     * @param id
     * @param supplierCompanyId
     */
    public removeSupplier(id: string, supplierCompanyId: string): Observable<DemandSupplier[]> {
        return this.http
            .delete<IDemandSupplier[]>(this.getBase(id, 'supplier', supplierCompanyId).toString())
            .pipe(map((suppliers) => suppliers.map((s) => DemandSupplier.instance(s, this.i18n))));
    }

    /**
     * Changes the contact person of a supplier that is already assigned to the demand without
     * removing and re-adding the supplier. The new contact must belong to the same supplier
     * company; delivery condition and payment term of the supplier are kept.
     * @param id - demand id
     * @param supplierCompanyId - company id of the assigned supplier
     * @param contactId - id of the new contact person of the same supplier company
     */
    public updateSupplierContact(
        id: string,
        supplierCompanyId: string,
        contactId: string,
    ): Observable<DemandSupplier[]> {
        return this.http
            .put<
                IDemandSupplier[]
            >(this.getBase(id, 'supplier', supplierCompanyId, 'contact').toString(), { contactId })
            .pipe(map((suppliers) => suppliers.map((s) => DemandSupplier.instance(s, this.i18n))));
    }

    /**
     * Changes the shipping address to another predefined address
     * @param id
     * @param shippingAddressId
     */
    public changeShippingAddress(id: string, shippingAddressId: number): Observable<DemandShippingAddressChange> {
        return this.http
            .post<IDemandShippingAddressState>(this.getBase(id, 'shipping-address').toString(), {
                addressId: shippingAddressId,
            })
            .pipe(map((dto) => new DemandShippingAddressChange(dto)));
    }

    /**
     * Changes the shipping address to a custom address typed in by the user.
     * For this the current user probably needs privileges. Make sure to check them beforehand.
     * @param id
     * @param address
     */
    public changeShippingAddressToCustom(id: string, address: ShippingAddress): Observable<DemandShippingAddressChange> {
        return this.http
            .post<IDemandShippingAddressState>(
                this.getBase(id, 'shipping-address', 'custom').toString(),
                address.toFreeAddressDto(),
            )
            .pipe(map((dto) => new DemandShippingAddressChange(dto)));
    }

    /**
     * Updates the company code for a demand
     * @param id
     * @param companyCodeId
     */
    public updateCompanyCode(id: string, companyCodeId: string): Observable<DemandShippingAddressChange> {
        return this.http
            .post<IDemandShippingAddressState>(this.getBase(id, 'company-code').toString(), { companyCodeId })
            .pipe(map((dto) => new DemandShippingAddressChange(dto)));
    }

    /**
     * EPROC-10907: sets the customer site (WERKS) on a demand item; null clears it. The server re-derives the
     * shipping address from the position sites and answers with it.
     */
    public updateItemSite(id: string, itemId: string, siteCode: string | null): Observable<DemandShippingAddressChange> {
        return this.http
            .post<IDemandShippingAddressState>(this.getBase(id, 'item', itemId, 'site').toString(), { siteCode })
            .pipe(map((dto) => new DemandShippingAddressChange(dto)));
    }

    /**
     * Returns if a task should be displayed after announcing a demand
     */
    public showTaskAfterDemandAnnouncement(): Observable<boolean> {
        const uri: UriBuilder = this.env.getBase('workflow', 'setting', 'post-demand-task');

        return this.http.get<boolean>(uri.toString());
    }

    public startListeningToHintUpdates(): void {
        this.rfqSettingsService.purchaseRequisitionActive().pipe(
            tap((isActive: IPurchaseRequisitionActive) => {
                if (isActive.active) {
                    this.initSseListener();
                }
            }),
            switchMap((isActive: IPurchaseRequisitionActive) => {
                if (isActive.active) {
                    this.hasUnannouncedDemand().pipe(
                        switchMap((hasUnannouncedDemand: boolean) => this.getUpdatedHints(hasUnannouncedDemand)),
                    );
                }

                return this.getUpdatedHints(false);
            }),
        );
    }

    public hasUnannouncedDemand(): Observable<boolean> {
        return this.http
            .get<IUnannouncedDemandExists>(this.getBase('has-unannounced').toString(), {
                params: HttpExtras.IGNORE_CACHE,
            })
            .pipe(map((dto) => dto.exists));
    }

    public createNewDemand(existingDemandId: string): Observable<IDemandId> {
        return this.http.post<IDemandId>(this.getBase(existingDemandId, 'create', 'from-demand').toString(), {});
    }

    public createDemandFromRfq(rfqId: number): Observable<void> {
        return this.createDemandFromRfqSource({ rfqId, fromRfqItems: true });
    }

    public createDemandFromQuotation(rfqId: number, selectedQuotationItemIds: number[]): Observable<void> {
        return this.createDemandFromRfqSource({ rfqId, fromRfqItems: false, selectedQuotationItemIds });
    }

    private createDemandFromRfqSource(body: ICreateDemandFromRfqRequest): Observable<void> {
        return this.http.post<void>(this.getBase('create', 'from-rfq').toString(), body, {
            params: new HttpExtras({ customErrorhandling: true }),
        });
    }

    public createDraftFromPdf(file: File, companyCodeId?: string): Observable<IDemandDraftFromPdfOutcome> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        if (companyCodeId) {
            formData.append('companyCodeId', companyCodeId);
        }

        return this.http.post<IDemandDraftFromPdfOutcome>(
            this.getBase('v2', 'create', 'from-pdf').toString(),
            formData,
        );
    }

    /**
     * Second call after an AMBIGUOUS outcome: re-analyze the same PDF with the supplier the user
     * explicitly chose (and optionally the confirmed contact). Always creates a draft or fails.
     */
    public createDraftFromPdfForSupplier(
        file: File,
        companyCodeId: string,
        supplierCompanyId: string,
        supplierContactId?: string,
    ): Observable<IDemandId> {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('companyCodeId', companyCodeId);
        if (supplierContactId) {
            formData.append('supplierContactId', supplierContactId);
        }

        return this.http.post<IDemandId>(
            this.getBase('create', 'from-pdf', 'supplier', supplierCompanyId).toString(),
            formData,
        );
    }

    /**
     * Run the PDF analysis and track the running state, so callers can warn that a new analysis
     * would overwrite the one currently in progress. Only a RESOLVED outcome actually created a
     * draft, so only that emits `unannouncedDemandChanged$`.
     */
    public analyzePdfToDraft(file: File, companyCodeId?: string): Observable<IDemandDraftFromPdfOutcome> {
        this._pdfAnalysisRunning.set(true);

        return this.createDraftFromPdf(file, companyCodeId).pipe(
            tap((outcome) => {
                if (outcome.status === 'RESOLVED') {
                    this._unannouncedDemandChanged$.next();
                }
            }),
            finalize(() => this._pdfAnalysisRunning.set(false)),
        );
    }

    /** Like {@link analyzePdfToDraft}, but for the second run with an explicitly chosen supplier. */
    public analyzePdfToDraftForSupplier(
        file: File,
        companyCodeId: string,
        supplierCompanyId: string,
        supplierContactId?: string,
    ): Observable<IDemandId> {
        this._pdfAnalysisRunning.set(true);

        return this.createDraftFromPdfForSupplier(file, companyCodeId, supplierCompanyId, supplierContactId).pipe(
            tap(() => this._unannouncedDemandChanged$.next()),
            finalize(() => this._pdfAnalysisRunning.set(false)),
        );
    }

    public getAdditionalCosts(demandId: string): Observable<DemandItemAdditionalCostInfo> {
        const uri = this.env.getBase('demand', demandId, 'additional-costs');

        return this.http
            .get<IDemandAdditionalCostInfo>(uri.toString(), { params: HttpExtras.IGNORE_CACHE })
            .pipe(map((dto) => new DemandItemAdditionalCostInfo(dto)));
    }

    afterLoginInit(): void {
        this.startListeningToHintUpdates();
    }

    private getUpdatedHints(hasUnannouncedDemand: boolean): Observable<NavigationHints> {
        return this.navigationHintService.hints$.pipe(
            map((hints: NavigationHints) => {
                const newHints = hints.copy();
                if (!hasUnannouncedDemand) {
                    newHints.remove(EprocNavigationModule.CLIENT_PURCHASE_REQUISITION_LIST);
                } else if (
                    hasUnannouncedDemand &&
                    !newHints.has(EprocNavigationModule.CLIENT_PURCHASE_REQUISITION_LIST)
                ) {
                    newHints.add(
                        new NavigationHint(1, 'accent', EprocNavigationModule.CLIENT_PURCHASE_REQUISITION_LIST),
                    );
                }

                return newHints;
            }),
            take(1),
        );
    }

    private initSseListener(): void {
        this.sseService
            .getEventsForType<IUnannouncedDemandExists>(SseDataType.UnannouncedDemandExists)
            .pipe(
                switchMap((hasUnannouncedDemand: IUnannouncedDemandExists) =>
                    this.getUpdatedHints(hasUnannouncedDemand.exists),
                ),
            )
            .subscribe((hints: NavigationHints) => this.navigationHintService.setHints(hints));
    }

    /**
     * Override the purchasing group of a single demand item from within a release task (UC-EKG-13).
     */
    public updatePurchasingGroupForTask(
        taskId: string,
        demandId: string,
        demandItemId: string,
        purchasingGroupCode: string,
    ): Observable<DemandProductDto> {
        const uri: UriBuilder = this.env.getBase(
            'task',
            taskId,
            'demand',
            demandId,
            'item-purchasing-group',
            demandItemId,
        );

        return this.http.put<DemandProductDto>(uri.toString(), { purchasingGroupCode });
    }

    /**
     * Clear the purchasing group of a single demand item from within a release task (UC-EKG-13).
     */
    public deletePurchasingGroupForTask(
        taskId: string,
        demandId: string,
        demandItemId: string,
    ): Observable<DemandProductDto> {
        const uri: UriBuilder = this.env.getBase(
            'task',
            taskId,
            'demand',
            demandId,
            'item-purchasing-group',
            demandItemId,
        );

        return this.http.delete<DemandProductDto>(uri.toString());
    }

    public applyAccountingToAllItems(
        demandId: string,
        accountingRequests: {
            type: string;
            customAccountingTypeId?: number;
            _value?: { id: number; key: string; name: string; description: string };
        }[],
        overwriteExisting: boolean,
        siteCode?: string | null,
        purchasingGroupCode?: string,
        materialGroupCode?: string,
    ): Observable<DemandShippingAddressChange | null> {
        return this.http
            .put<IDemandShippingAddressState | null>(this.getBase(demandId, 'accounting').toString(), {
                accountingRequests,
                overwriteExisting,
                siteCode,
                purchasingGroupCode,
                materialGroupCode,
            })
            .pipe(map((dto) => (dto ? new DemandShippingAddressChange(dto) : null)));
    }

    private getBase(...pathSegments: (string | number)[]): UriBuilder {
        return this.env.getBase('demand', ...pathSegments);
    }
}
